import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import BudgetRangeSlider from '@/components/BudgetRangeSlider';
import IndiaLocationSelector from '@/components/IndiaLocationSelector';
import { useCountry } from '@/context/CountryContext';
import { ChevronRight, ChevronLeft, Check, Sparkles, Search, Home, Car, Watch, Ship, Plane, Users, Building, Eye, Palette, AlertCircle, HelpCircle, Layers, Crown, BedDouble, MapPin, TreePine, Waves, Mountain, Gem, Sunset, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  COUNTRIES, CURRENCIES, GOAL_OPTIONS, RISK_OPTIONS, TIMELINE_OPTIONS,
  PROPERTY_TYPES, VIEW_TYPES, INTERIOR_STYLES, DUBAI_LOCATIONS,
  INDIA_GOAL_OPTIONS, INDIA_RISK_OPTIONS, INDIA_TIMELINE_OPTIONS,
  INDIA_PROPERTY_TYPES, INDIA_VIEW_TYPES, INDIA_INTERIOR_STYLES,
  INDIA_CAR_BRANDS, INDIA_WATCH_BRANDS, INDIA_LIFESTYLE_SERVICES,
  MAX_SELECT, LIFESTYLE_CARS, LIFESTYLE_WATCHES, YACHT_BRANDS, CONCIERGE_SERVICES,
} from '@/components/onboarding/onboardingData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


// Selectable Card Component
const SelectCard = ({ selected, onClick, children, testId, className = '' }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`group relative p-4 rounded-xl border-2 transition-all duration-300 text-left overflow-hidden ${
      selected
        ? 'border-gold bg-gold/10 shadow-[0_0_20px_rgba(212,175,55,0.15)]'
        : 'border-white/10 hover:border-gold/40 hover:bg-white/5'
    } ${className}`}
  >
    {selected && <div className="absolute top-2 right-2"><Check className="w-4 h-4 text-gold" /></div>}
    {children}
  </button>
);

// Brand Chip (for lifestyle multi-select)
const BrandChip = ({ label, selected, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2 rounded-full text-sm font-medium transition-all border whitespace-nowrap ${
      selected
        ? 'border-gold bg-gold/15 text-gold shadow-[0_0_10px_rgba(212,175,55,0.1)]'
        : 'border-white/10 text-text-secondary hover:border-gold/30 hover:text-white'
    }`}
  >
    {label}
  </button>
);

// Inline Error
const StepError = ({ show }) => {
  if (!show) return null;
  return (
    <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
      className="flex items-center gap-2 text-rose-400 text-sm mt-2"
    >
      <AlertCircle className="w-4 h-4" /> Please complete this step
    </motion.div>
  );
};

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { countryCode, setCountry: setGlobalCountry } = useCountry();
  const isIndia = countryCode === 'india';
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [countrySearch, setCountrySearch] = useState(() => isIndia ? 'India' : 'United Arab Emirates');
  const [countryOpen, setCountryOpen] = useState(false);
  const countryRef = useRef(null);
  const [matchedItems, setMatchedItems] = useState([]);
  const [matchLoading, setMatchLoading] = useState(false);

  // Derive initial country/currency from context — NOT hardcoded to UAE
  const initCountry = isIndia ? 'India' : 'United Arab Emirates';
  const initCurrency = isIndia ? 'INR' : 'AED';

  // State — initialized from country context
  const [personal, setPersonal] = useState({
    country: initCountry,
    currency: initCurrency,
    budget_min: '', budget_max: '',
    car_budget_min: '', car_budget_max: '',
    lifestyle_budget_min: '', lifestyle_budget_max: '',
    annual_income: '',
    family_size: '',
    goal: '',
    risk_appetite: '',
    purchase_timeline: '',
  });

  const [propertyPrefs, setPropertyPrefs] = useState({
    property_types: [],
    view_types: [],
    interior_styles: [],
    bedrooms: 0,
    bathrooms: 0,
    preferred_locations: [],
  });

  const [lifestylePrefs, setLifestylePrefs] = useState({
    cars: [],
    watches: [],
    yachts: [],
    private_jets: '',
    concierge: [],
  });

  // Country search
  const filteredCountries = COUNTRIES.filter(c =>
    c.toLowerCase().startsWith(countrySearch.toLowerCase())
  );

  useEffect(() => {
    const handleClick = (e) => {
      if (countryRef.current && !countryRef.current.contains(e.target)) setCountryOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectCountry = (c) => {
    const currMap = { 'United Arab Emirates': 'AED', 'India': 'INR', 'United States': 'USD', 'United Kingdom': 'GBP' };
    const codeMap = { 'United Arab Emirates': 'uae', 'India': 'india' };
    setPersonal(p => ({ ...p, country: c, currency: currMap[c] || p.currency }));
    setCountrySearch(c);
    setCountryOpen(false);
    // Sync global country context
    const code = codeMap[c];
    if (code) setGlobalCountry(code);
  };

  const handleCurrencyChange = (code) => {
    setPersonal(p => ({ ...p, currency: code, budget_min: '', budget_max: '', car_budget_min: '', car_budget_max: '', lifestyle_budget_min: '', lifestyle_budget_max: '' }));
  };

  // Toggle helpers with max selection + "not_decided" exclusivity
  const toggleArr = (setter, key, val, max = 0) => {
    setter(prev => {
      const arr = prev[key];
      if (arr.includes(val)) {
        return { ...prev, [key]: arr.filter(v => v !== val) };
      }
      // "not_decided" is exclusive — clears all others
      if (val === 'not_decided') {
        return { ...prev, [key]: ['not_decided'] };
      }
      // Selecting a real option removes "not_decided"
      const filtered = arr.filter(v => v !== 'not_decided');
      if (max > 0 && filtered.length >= max) return prev; // at limit
      return { ...prev, [key]: [...filtered, val] };
    });
  };

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [step]);

  // Validation
  const isStep1Valid = () =>
    personal.goal && personal.risk_appetite && personal.purchase_timeline &&
    personal.country && personal.currency && personal.annual_income && personal.family_size &&
    personal.budget_min && personal.budget_max;

  const isStep2Valid = () =>
    propertyPrefs.property_types.length > 0 && propertyPrefs.view_types.length > 0 && propertyPrefs.interior_styles.length > 0;

  const isStep3Valid = () =>
    lifestylePrefs.cars.length > 0 && lifestylePrefs.watches.length > 0;

  const canProceed = () => {
    if (step === 1) return isStep1Valid();
    if (step === 2) return isStep2Valid();
    if (step === 3) return isStep3Valid();
    return true;
  };

  const handleNext = () => {
    if (!canProceed()) { setShowErrors(true); return; }
    setShowErrors(false);
    setStep(step + 1);
  };

  const handleSubmit = async () => {
    if (!isStep3Valid()) { setShowErrors(true); return; }
    setLoading(true);
    try {
      
      await axios.post(`${API}/onboarding/profile`, {
        user_id: '',
        personal,
        home_preferences: {
          property_types: propertyPrefs.property_types,
          view_types: propertyPrefs.view_types,
          interior_styles: propertyPrefs.interior_styles,
          bedrooms: propertyPrefs.bedrooms,
          bathrooms: propertyPrefs.bathrooms,
          preferred_locations: propertyPrefs.preferred_locations,
        },
        lifestyle_preferences: {
          cars: lifestylePrefs.cars,
          watches: lifestylePrefs.watches,
          yachts: lifestylePrefs.yachts,
          private_jets: lifestylePrefs.private_jets,
          staff_needs: lifestylePrefs.concierge,
        },
      }, { withCredentials: true });
      setLoading(false);
      setStep(4);
      // Fetch AI matches
      setMatchLoading(true);
      try {
        const res = await axios.get(`${API}/bundles`, { withCredentials: true });
        const bundles = res.data.bundles || [];
        if (bundles.length > 0) {
          const best = bundles.find(b => b.featured) || bundles[0];
          setMatchedItems(best.items.filter(i => i.item).slice(0, 3));
        }
      } catch (_) { /* non-critical */ }
      setMatchLoading(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save. Please try again.');
      setLoading(false);
    }
  };

  const TOTAL_STEPS = 3;
  const progress = step === 0 ? 0 : step >= 4 ? 100 : (step / TOTAL_STEPS) * 100;

  return (
    <div className="min-h-screen bg-obsidian text-text-primary">
      {/* Progress Header */}
      {step > 0 && (
        <div className="glass-card border-b border-white/5 sticky top-0 z-50">
          <div className="max-w-5xl mx-auto px-8 py-5">
            <div className="flex justify-between items-center mb-3">
              <h1 className="font-playfair text-2xl font-semibold text-gold">ASTIIV BASGAT</h1>
              <span className="text-text-secondary text-sm">{step >= 4 ? 'Profile Complete' : `Step ${step} of ${TOTAL_STEPS}`}</span>
            </div>
            <div className="w-full bg-surface h-1.5 rounded-full overflow-hidden">
              <motion.div className="h-full bg-gradient-to-r from-gold to-amber-400 rounded-full" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
            </div>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-8 py-12">
        <AnimatePresence mode="wait">

          {/* STEP 0: Welcome */}
          {step === 0 && (
            <motion.div key="s0" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="flex flex-col items-center justify-center min-h-[65vh] text-center"
            >
              <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2 }} className="mb-10">
                <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gold/10 flex items-center justify-center">
                  <Sparkles className="w-12 h-12 text-gold" />
                </div>
                <h1 className="font-playfair text-5xl md:text-6xl font-semibold mb-6">
                  Welcome to <span className="text-gold">ASTIIV BASGAT</span>
                </h1>
                <p className="text-text-secondary text-xl max-w-2xl mx-auto leading-relaxed mb-3">
                  {isIndia ? 'Plan your dream life — home, car, and lifestyle — smartly' : "Your gateway to Dubai's most exclusive properties & lifestyle"}
                </p>
                <p className="text-text-muted text-lg max-w-xl mx-auto">
                  {isIndia
                    ? "Tell us your income, goals, and preferences — our AI will build a realistic life plan tailored to your budget."
                    : "Answer a few questions and we'll match you with the perfect Dubai properties, luxury assets, and lifestyle experiences."}
                </p>
              </motion.div>

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="space-y-4 mb-12">
                {(isIndia ? [
                  { n: 1, t: 'Goals & Budget', d: 'Your income, savings goals, and budget' },
                  { n: 2, t: 'Dream Home', d: 'Property type, city, and preferences' },
                  { n: 3, t: 'Lifestyle Plan', d: 'Car, accessories, and lifestyle upgrades' },
                ] : [
                  { n: 1, t: 'Goals & Budget', d: 'Your goals, risk appetite, and budget' },
                  { n: 2, t: 'Dubai Property', d: 'Property type, views, and interior style' },
                  { n: 3, t: 'Luxury Lifestyle', d: 'Cars, watches, yachts, and concierge' },
                ]).map(({ n, t, d }) => (
                  <div key={n} className="flex items-center gap-4 text-left">
                    <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-gold font-bold">{n}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{t}</h3>
                      <p className="text-text-secondary text-sm">{d}</p>
                    </div>
                  </div>
                ))}
              </motion.div>

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
                <Button onClick={() => setStep(1)} className="bg-gold text-black hover:bg-gold-light uppercase tracking-widest text-sm font-bold px-12 py-6 rounded-sm" data-testid="onboarding-start-btn">
                  {isIndia ? 'Start Planning' : 'Begin'} <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
                <p className="text-text-muted text-sm mt-4">Takes about 3 minutes</p>
              </motion.div>
            </motion.div>
          )}

          {/* STEP 1: Goals & Budget */}
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} className="space-y-8">
              <div>
                <h2 className="font-playfair text-4xl font-semibold mb-2">Goals & Budget</h2>
                <p className="text-text-secondary text-lg">Tell us what you're looking for</p>
              </div>

              {/* Goal */}
              <div className="glass-card p-6 rounded-xl border border-gold/20">
                <Label className="text-gold text-lg mb-4 block font-semibold">{isIndia ? "What's your primary goal?" : 'What brings you to Dubai?'}</Label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {(isIndia ? INDIA_GOAL_OPTIONS : GOAL_OPTIONS).map(o => (
                    <SelectCard key={o.value} selected={personal.goal === o.value} onClick={() => setPersonal(p => ({ ...p, goal: o.value }))} testId={`goal-${o.value}`}>
                      <o.icon className={`w-6 h-6 mb-2 ${personal.goal === o.value ? 'text-gold' : 'text-text-secondary'}`} />
                      <div className="font-semibold">{o.label}</div>
                      <div className="text-text-secondary text-xs mt-1">{o.desc}</div>
                    </SelectCard>
                  ))}
                </div>
                {showErrors && !personal.goal && <StepError show />}
              </div>

              {/* Risk */}
              <div className="glass-card p-6 rounded-xl border border-white/10">
                <Label className="text-gold text-lg mb-4 block font-semibold">{isIndia ? 'Investment Style' : 'Risk Appetite'}</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {(isIndia ? INDIA_RISK_OPTIONS : RISK_OPTIONS).map(o => (
                    <SelectCard key={o.value} selected={personal.risk_appetite === o.value} onClick={() => setPersonal(p => ({ ...p, risk_appetite: o.value }))} testId={`risk-${o.value}`}>
                      <div className="font-semibold">{o.label}</div>
                      <div className="text-text-secondary text-xs mt-1">{o.desc}</div>
                    </SelectCard>
                  ))}
                </div>
                {showErrors && !personal.risk_appetite && <StepError show />}
              </div>

              {/* Timeline */}
              <div className="glass-card p-6 rounded-xl border border-white/10">
                <Label className="text-gold text-lg mb-4 block font-semibold">{isIndia ? 'When do you plan to buy?' : 'Purchase Timeline'}</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {(isIndia ? INDIA_TIMELINE_OPTIONS : TIMELINE_OPTIONS).map(o => (
                    <SelectCard key={o.value} selected={personal.purchase_timeline === o.value} onClick={() => setPersonal(p => ({ ...p, purchase_timeline: o.value }))} testId={`timeline-${o.value}`} className="!p-3 text-center">
                      <div className="font-medium text-sm">{o.label}</div>
                    </SelectCard>
                  ))}
                </div>
                {showErrors && !personal.purchase_timeline && <StepError show />}
              </div>

              {/* Country + Currency */}
              <div className="grid md:grid-cols-2 gap-6">
                <div ref={countryRef} className="relative">
                  <Label className="text-text-primary mb-2 block">Country</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary pointer-events-none" />
                    <input
                      type="text"
                      value={countrySearch || personal.country}
                      onChange={(e) => { setCountrySearch(e.target.value); setCountryOpen(true); }}
                      onFocus={() => { setCountrySearch(''); setCountryOpen(true); }}
                      placeholder="Search country..."
                      className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none pl-9 py-3 text-white"
                      data-testid="country-search-input"
                    />
                  </div>
                  {countryOpen && (
                    <div className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto bg-[#1a1a2e] border border-white/10 rounded-lg shadow-2xl">
                      {filteredCountries.map(c => (
                        <button key={c} onClick={() => selectCountry(c)}
                          className="w-full text-left px-4 py-2.5 hover:bg-gold/10 text-sm text-white transition-colors"
                          data-testid={`country-opt-${c.replace(/\s/g,'-').toLowerCase()}`}
                        >{c}</button>
                      ))}
                      {filteredCountries.length === 0 && <div className="px-4 py-3 text-text-secondary text-sm">No match</div>}
                    </div>
                  )}
                  {showErrors && !personal.country && <StepError show />}
                </div>

                <div>
                  <Label className="text-text-primary mb-2 block">Preferred Currency</Label>
                  <div className="flex gap-2 flex-wrap">
                    {CURRENCIES.map(c => (
                      <button key={c.code} onClick={() => handleCurrencyChange(c.code)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${personal.currency === c.code ? 'border-gold bg-gold/10 text-gold' : 'border-white/10 text-text-secondary hover:border-gold/30'}`}
                        data-testid={`currency-${c.code}`}
                      >{c.symbol} {c.code}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Budgets */}
              <div className="space-y-6">
                <div className="glass-card p-6 rounded-xl border border-white/10">
                  <Label className="text-text-primary mb-3 block text-lg">Property Budget</Label>
                  <BudgetRangeSlider currency={personal.currency} minValue={0} maxValue={0} onChange={({ min, max }) => setPersonal(p => ({ ...p, budget_min: String(min), budget_max: String(max) }))} />
                </div>
                <div className="glass-card p-6 rounded-xl border border-white/10">
                  <Label className="text-text-primary mb-3 block text-lg">Car Budget</Label>
                  <BudgetRangeSlider currency={personal.currency} minValue={0} maxValue={0} onChange={({ min, max }) => setPersonal(p => ({ ...p, car_budget_min: String(min), car_budget_max: String(max) }))} />
                </div>
                <div className="glass-card p-6 rounded-xl border border-white/10">
                  <Label className="text-text-primary mb-3 block text-lg">Lifestyle Budget <span className="text-sm text-text-secondary font-normal">(watches, accessories)</span></Label>
                  <BudgetRangeSlider currency={personal.currency} minValue={0} maxValue={0} onChange={({ min, max }) => setPersonal(p => ({ ...p, lifestyle_budget_min: String(min), lifestyle_budget_max: String(max) }))} />
                </div>
              </div>

              {/* Income + Family */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-text-primary mb-2 block">Annual Income ({personal.currency})</Label>
                  <Input type="number" value={personal.annual_income} onChange={(e) => setPersonal(p => ({ ...p, annual_income: e.target.value }))}
                    placeholder="Enter amount" className="bg-transparent border-b border-white/20 focus:border-gold outline-none rounded-none px-0 py-4" data-testid="income-input" />
                  {showErrors && !personal.annual_income && <StepError show />}
                </div>
                <div>
                  <Label className="text-text-primary mb-2 block">Family Size</Label>
                  <Input type="number" value={personal.family_size} onChange={(e) => setPersonal(p => ({ ...p, family_size: e.target.value }))}
                    placeholder="e.g. 4" className="bg-transparent border-b border-white/20 focus:border-gold outline-none rounded-none px-0 py-4" data-testid="family-input" />
                  {showErrors && !personal.family_size && <StepError show />}
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 2: Dubai Property Preferences */}
          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} className="space-y-8">
              <div>
                <h2 className="font-playfair text-4xl font-semibold mb-2">{isIndia ? 'Your Dream Home' : 'Dubai Property Preferences'}</h2>
                <p className="text-text-secondary text-lg">{isIndia ? 'What kind of property are you looking for?' : 'What type of Dubai property do you prefer?'}</p>
              </div>

              {/* Property Type */}
              <div className="glass-card p-6 rounded-xl border border-gold/20">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-gold text-lg font-semibold flex items-center gap-2"><Building className="w-5 h-5" /> Property Type</Label>
                  <span className="text-xs text-text-secondary">{propertyPrefs.property_types.filter(v => v !== 'not_decided').length}/{MAX_SELECT} selected</span>
                </div>
                <p className="text-text-secondary text-sm mb-4">Select up to {MAX_SELECT}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {(isIndia ? INDIA_PROPERTY_TYPES : PROPERTY_TYPES).map(t => {
                    const sel = propertyPrefs.property_types.includes(t.value);
                    const atLimit = !sel && t.value !== 'not_decided' && propertyPrefs.property_types.filter(v => v !== 'not_decided').length >= MAX_SELECT;
                    return (
                      <SelectCard key={t.value} selected={sel} onClick={() => !atLimit && toggleArr(setPropertyPrefs, 'property_types', t.value, MAX_SELECT)} testId={`prop-${t.value}`}
                        className={atLimit ? 'opacity-40 cursor-not-allowed' : ''}>
                        <t.icon className={`w-7 h-7 mb-2 ${sel ? 'text-gold' : 'text-text-secondary group-hover:text-gold/60'} transition-colors`} />
                        <div className="font-semibold">{t.label}</div>
                      </SelectCard>
                    );
                  })}
                </div>
                {showErrors && propertyPrefs.property_types.length === 0 && <StepError show />}
              </div>

              {/* Bedrooms + Bathrooms */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="glass-card p-6 rounded-xl border border-white/10">
                  <Label className="text-gold text-lg mb-4 block font-semibold flex items-center gap-2"><BedDouble className="w-5 h-5" /> Bedrooms</Label>
                  <div className="flex items-center justify-center gap-6" data-testid="bedrooms-stepper">
                    <button onClick={() => setPropertyPrefs(p => ({ ...p, bedrooms: Math.max(0, p.bedrooms - 1) }))}
                      className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors" data-testid="bedrooms-minus">
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="text-3xl font-semibold text-gold min-w-[3ch] text-center" data-testid="bedrooms-value">{propertyPrefs.bedrooms || 'Any'}</span>
                    <button onClick={() => setPropertyPrefs(p => ({ ...p, bedrooms: Math.min(10, p.bedrooms + 1) }))}
                      className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors" data-testid="bedrooms-plus">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-text-secondary text-xs text-center mt-2">{propertyPrefs.bedrooms === 0 ? 'No preference' : `${propertyPrefs.bedrooms}+ bedrooms`}</p>
                </div>
                <div className="glass-card p-6 rounded-xl border border-white/10">
                  <Label className="text-gold text-lg mb-4 block font-semibold flex items-center gap-2"><Home className="w-5 h-5" /> Bathrooms</Label>
                  <div className="flex items-center justify-center gap-6" data-testid="bathrooms-stepper">
                    <button onClick={() => setPropertyPrefs(p => ({ ...p, bathrooms: Math.max(0, p.bathrooms - 1) }))}
                      className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors" data-testid="bathrooms-minus">
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="text-3xl font-semibold text-gold min-w-[3ch] text-center" data-testid="bathrooms-value">{propertyPrefs.bathrooms || 'Any'}</span>
                    <button onClick={() => setPropertyPrefs(p => ({ ...p, bathrooms: Math.min(8, p.bathrooms + 1) }))}
                      className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors" data-testid="bathrooms-plus">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-text-secondary text-xs text-center mt-2">{propertyPrefs.bathrooms === 0 ? 'No preference' : `${propertyPrefs.bathrooms}+ bathrooms`}</p>
                </div>
              </div>

              {/* Preferred Locations */}
              <div className="glass-card p-6 rounded-xl border border-white/10">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-gold text-lg font-semibold flex items-center gap-2"><MapPin className="w-5 h-5" /> {isIndia ? 'Preferred Locations' : 'Preferred Dubai Locations'}</Label>
                  <span className="text-xs text-text-secondary">{propertyPrefs.preferred_locations.length} selected</span>
                </div>
                <p className="text-text-secondary text-sm mb-4">{isIndia ? 'Select city and localities you prefer' : 'Select areas you\'re interested in (optional)'}</p>
                {isIndia ? (
                  <IndiaLocationSelector
                    selected={propertyPrefs.preferred_locations}
                    onChange={(next) => setPropertyPrefs(p => ({ ...p, preferred_locations: next }))}
                  />
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {DUBAI_LOCATIONS.map(loc => {
                      const sel = propertyPrefs.preferred_locations.includes(loc);
                      return (
                        <button key={loc} onClick={() => toggleArr(setPropertyPrefs, 'preferred_locations', loc)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all border whitespace-nowrap ${
                            sel ? 'border-gold bg-gold/15 text-gold shadow-[0_0_10px_rgba(212,175,55,0.1)]' : 'border-white/10 text-text-secondary hover:border-gold/30 hover:text-white'
                          }`} data-testid={`loc-${loc.replace(/\s/g,'-').toLowerCase()}`}>{loc}</button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* View Type */}
              <div className="glass-card p-6 rounded-xl border border-white/10">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-gold text-lg font-semibold flex items-center gap-2"><Eye className="w-5 h-5" /> Preferred View</Label>
                  <span className="text-xs text-text-secondary">{propertyPrefs.view_types.filter(v => v !== 'not_decided').length}/{MAX_SELECT} selected</span>
                </div>
                <p className="text-text-secondary text-sm mb-4">Select up to {MAX_SELECT}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {(isIndia ? INDIA_VIEW_TYPES : VIEW_TYPES).map(v => {
                    const sel = propertyPrefs.view_types.includes(v.value);
                    const atLimit = !sel && v.value !== 'not_decided' && propertyPrefs.view_types.filter(x => x !== 'not_decided').length >= MAX_SELECT;
                    return (
                      <SelectCard key={v.value} selected={sel} onClick={() => !atLimit && toggleArr(setPropertyPrefs, 'view_types', v.value, MAX_SELECT)} testId={`view-${v.value}`}
                        className={atLimit ? 'opacity-40 cursor-not-allowed' : ''}>
                        <v.icon className={`w-6 h-6 mb-2 ${sel ? 'text-gold' : 'text-text-secondary group-hover:text-gold/60'} transition-colors`} />
                        <div className="font-semibold">{v.label}</div>
                      </SelectCard>
                    );
                  })}
                </div>
                {showErrors && propertyPrefs.view_types.length === 0 && <StepError show />}
              </div>

              {/* Interior Style */}
              <div className="glass-card p-6 rounded-xl border border-white/10">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-gold text-lg font-semibold flex items-center gap-2"><Palette className="w-5 h-5" /> Interior Style</Label>
                  <span className="text-xs text-text-secondary">{propertyPrefs.interior_styles.filter(v => v !== 'not_decided').length}/{MAX_SELECT} selected</span>
                </div>
                <p className="text-text-secondary text-sm mb-4">Select up to {MAX_SELECT}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {(isIndia ? INDIA_INTERIOR_STYLES : INTERIOR_STYLES).map(s => {
                    const sel = propertyPrefs.interior_styles.includes(s.value);
                    const atLimit = !sel && s.value !== 'not_decided' && propertyPrefs.interior_styles.filter(x => x !== 'not_decided').length >= MAX_SELECT;
                    return (
                      <SelectCard key={s.value} selected={sel} onClick={() => !atLimit && toggleArr(setPropertyPrefs, 'interior_styles', s.value, MAX_SELECT)} testId={`style-${s.value}`}
                        className={atLimit ? 'opacity-40 cursor-not-allowed' : ''}>
                        <s.icon className={`w-6 h-6 mb-2 ${sel ? 'text-gold' : 'text-text-secondary group-hover:text-gold/60'} transition-colors`} />
                        <div className="font-semibold">{s.label}</div>
                      </SelectCard>
                    );
                  })}
                </div>
                {showErrors && propertyPrefs.interior_styles.length === 0 && <StepError show />}
              </div>
            </motion.div>
          )}

          {/* STEP 3: Lifestyle Preferences */}
          {step === 3 && (
            <motion.div key="s3" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} className="space-y-8">
              <div>
                <h2 className="font-playfair text-4xl font-semibold mb-2">{isIndia ? 'Lifestyle Plan' : 'Luxury Lifestyle'}</h2>
                <p className="text-text-secondary text-lg">{isIndia ? 'Choose your lifestyle preferences' : 'Choose your lifestyle interests'}</p>
              </div>

              {/* Cars */}
              <div className="glass-card p-6 rounded-xl border border-gold/20">
                <Label className="text-gold text-lg mb-1 block font-semibold flex items-center gap-2"><Car className="w-5 h-5" /> {isIndia ? 'Car Brands' : 'Luxury Cars'}</Label>
                <p className="text-text-secondary text-sm mb-4">Select brands you love</p>
                <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto pr-2 scrollbar-thin" data-testid="cars-scroll">
                  {(isIndia ? INDIA_CAR_BRANDS : LIFESTYLE_CARS).map(b => (
                    <BrandChip key={b} label={b} selected={lifestylePrefs.cars.includes(b)} onClick={() => toggleArr(setLifestylePrefs, 'cars', b)} testId={`car-${b.replace(/\s/g,'-').toLowerCase()}`} />
                  ))}
                </div>
                {showErrors && lifestylePrefs.cars.length === 0 && <StepError show />}
              </div>

              {/* Watches */}
              <div className="glass-card p-6 rounded-xl border border-white/10">
                <Label className="text-gold text-lg mb-1 block font-semibold flex items-center gap-2"><Watch className="w-5 h-5" /> {isIndia ? 'Watches & Accessories' : 'Premium Watches'}</Label>
                <p className="text-text-secondary text-sm mb-4">Select brands you love</p>
                <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto pr-2 scrollbar-thin" data-testid="watches-scroll">
                  {(isIndia ? INDIA_WATCH_BRANDS : LIFESTYLE_WATCHES).map(b => (
                    <BrandChip key={b} label={b} selected={lifestylePrefs.watches.includes(b)} onClick={() => toggleArr(setLifestylePrefs, 'watches', b)} testId={`watch-${b.replace(/\s/g,'-').toLowerCase()}`} />
                  ))}
                </div>
                {showErrors && lifestylePrefs.watches.length === 0 && <StepError show />}
              </div>

              {/* Yachts — only for UAE */}
              {!isIndia && (
              <div className="glass-card p-6 rounded-xl border border-white/10">
                <Label className="text-gold text-lg mb-1 block font-semibold flex items-center gap-2"><Ship className="w-5 h-5" /> Yachts</Label>
                <p className="text-text-secondary text-sm mb-4">Select brands (optional)</p>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-2 scrollbar-thin" data-testid="yachts-scroll">
                  {YACHT_BRANDS.map(b => (
                    <BrandChip key={b} label={b} selected={lifestylePrefs.yachts.includes(b)} onClick={() => toggleArr(setLifestylePrefs, 'yachts', b)} testId={`yacht-${b.replace(/\s/g,'-').toLowerCase()}`} />
                  ))}
                </div>
              </div>
              )}

              {/* Private Jets — only for UAE */}
              {!isIndia && (
              <div className="glass-card p-6 rounded-xl border border-white/10">
                <Label className="text-gold text-lg mb-4 block font-semibold flex items-center gap-2"><Plane className="w-5 h-5" /> Private Jets / Charter</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: 'ownership', label: 'Ownership' },
                    { value: 'charter', label: 'Charter Only' },
                    { value: 'interested', label: 'Interested' },
                    { value: 'no', label: 'Not Interested' },
                  ].map(o => (
                    <SelectCard key={o.value} selected={lifestylePrefs.private_jets === o.value} onClick={() => setLifestylePrefs(p => ({ ...p, private_jets: o.value }))} testId={`jet-${o.value}`} className="!p-3 text-center">
                      <div className="font-medium text-sm">{o.label}</div>
                    </SelectCard>
                  ))}
                </div>
              </div>
              )}

              {/* Concierge / Services */}
              <div className="glass-card p-6 rounded-xl border border-gold/20">
                <Label className="text-gold text-lg mb-1 block font-semibold flex items-center gap-2"><Users className="w-5 h-5" /> {isIndia ? 'Lifestyle Services' : 'Concierge Services'}</Label>
                <p className="text-text-secondary text-sm mb-4">Select services you might need</p>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-2 scrollbar-thin" data-testid="concierge-scroll">
                  {(isIndia ? INDIA_LIFESTYLE_SERVICES : CONCIERGE_SERVICES).map(s => (
                    <BrandChip key={s} label={s} selected={lifestylePrefs.concierge.includes(s)} onClick={() => toggleArr(setLifestylePrefs, 'concierge', s)} testId={`svc-${s.replace(/\s/g,'-').toLowerCase()}`} />
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 4: Smart Match Preview */}
          {step === 4 && (
            <motion.div key="s4" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }} className="space-y-8"
            >
              <div className="text-center pt-8">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="w-20 h-20 mx-auto mb-6 rounded-full bg-gold/15 flex items-center justify-center">
                  <Sparkles className="w-10 h-10 text-gold" />
                </motion.div>
                <h2 className="font-playfair text-4xl md:text-5xl font-semibold mb-3">Your AI Matches Are Ready</h2>
                <p className="text-text-secondary text-lg max-w-xl mx-auto">
                  Based on your profile, here's a preview of what ASTIIV BASGAT found for you
                </p>
              </div>

              {matchLoading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-4" data-testid="match-loading">
                  <div className="w-10 h-10 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
                  <p className="text-text-secondary text-sm animate-pulse">Matching assets to your dream lifestyle...</p>
                </div>
              ) : matchedItems.length > 0 ? (
                <div className="grid gap-4 max-w-2xl mx-auto" data-testid="match-preview-grid">
                  {matchedItems.map((item, idx) => {
                    const asset = item.item;
                    const img = (asset.images && asset.images[0]) || asset.image || '';
                    const price = asset.price || asset.price_from || 0;
                    const fmtPrice = price >= 1e6 ? `AED ${(price / 1e6).toFixed(1)}M` : price >= 1e3 ? `AED ${(price / 1e3).toFixed(0)}K` : `AED ${price.toLocaleString()}`;
                    return (
                      <motion.div key={`preview-${idx}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + idx * 0.15 }}
                        className="flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-white/[0.02] hover:border-gold/30 transition-all"
                        data-testid={`match-card-${idx}`}
                      >
                        <div className="w-20 h-20 rounded-xl overflow-hidden bg-white/5 flex-shrink-0 border border-white/5">
                          {img ? (
                            <img src={img} alt={asset.title || asset.model || ''} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              {item.type === 'property' ? <Home className="w-6 h-6 text-gold/40" /> :
                               item.type === 'car' ? <Car className="w-6 h-6 text-gold/40" /> :
                               <Watch className="w-6 h-6 text-gold/40" />}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] text-gold/60 uppercase tracking-widest mb-0.5">{item.type}</div>
                          <div className="font-semibold text-sm truncate">{asset.title || asset.model || asset.name || 'Premium Item'}</div>
                          <div className="text-text-secondary text-xs mt-0.5">{asset.location || asset.brand || ''}</div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-gold font-semibold text-sm">{fmtPrice}</div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12" data-testid="match-empty">
                  <p className="text-text-secondary">Your personalized matches are waiting on your dashboard.</p>
                </div>
              )}

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
                className="flex flex-col items-center gap-4 pt-4"
              >
                <Button onClick={() => navigate('/dashboard')}
                  className="bg-gold text-black hover:bg-gold-light uppercase tracking-widest text-sm font-bold px-12 py-6 rounded-sm"
                  data-testid="match-go-dashboard-btn"
                >
                  Explore Your Dashboard <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
                <p className="text-text-muted text-xs">You can always refine your preferences later</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        {step > 0 && step < 4 && (
          <div className="flex justify-between mt-12">
            <Button onClick={() => { setShowErrors(false); setStep(step - 1); }} variant="outline"
              className="border-2 border-white/20 text-white hover:border-gold hover:text-gold bg-transparent uppercase tracking-widest text-sm px-8"
              data-testid="onboarding-back-btn"
            >
              <ChevronLeft className="mr-2 h-4 w-4" /> Back
            </Button>

            {step < 3 ? (
              <Button onClick={handleNext}
                className={`ml-auto uppercase tracking-widest text-sm font-bold px-8 transition-all duration-300 ${canProceed() ? 'bg-gold text-black hover:bg-gold-light' : 'bg-gold/30 text-black/50 cursor-not-allowed'}`}
                data-testid="onboarding-next-btn"
              >
                Next <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading || !canProceed()}
                className={`ml-auto uppercase tracking-widest text-sm font-bold px-8 transition-all duration-300 ${canProceed() ? 'bg-gold text-black hover:bg-gold-light' : 'bg-gold/30 text-black/50 cursor-not-allowed'}`}
                data-testid="onboarding-submit-btn"
              >
                {loading ? 'Loading...' : 'Show My Dubai Matches'} <Check className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
